import type { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, ArrowDown, ArrowDownUp, ArrowUp } from "lucide-react";
import { useMemo } from "react";
import { HighlightedNarrative } from "@/shared/components/HighlightedNarrative/HighlightedNarrative";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { isActivityRequired, isTaskRequired } from "@/shared/utils/firm";

import adobeIcon from "../../../../assets/applications/AdobeIcon.svg";
import calendarIcon from "../../../../assets/applications/CalendarIcon.svg";
import chromeIcon from "../../../../assets/applications/ChromeIcon.svg";
import combineIcon from "../../../../assets/applications/CombineIcon.svg";
import combineIconWhite from "../../../../assets/applications/CombineIconWhite.svg";
import duplicateIcon from "../../../../assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "../../../../assets/applications/DuplicateIconWhite.svg";
import excelIcon from "../../../../assets/applications/ExcelIcon.svg";
import foxitIcon from "../../../../assets/applications/FoxitIcon.svg";
import kofaxIcon from "../../../../assets/applications/KofaxIcon.svg";
import outlookIcon from "../../../../assets/applications/OutlookIcon.svg";
import powerpointIcon from "../../../../assets/applications/PowerpointIcon.svg";
import teamsIcon from "../../../../assets/applications/TeamsIcon.svg";
import photosIcon from "../../../../assets/applications/WindowsPhotos.svg";
import wordIcon from "../../../../assets/applications/WordIcon.svg";
import zoomIcon from "../../../../assets/applications/ZoomIcon.svg";
import phoneIcon from "../../../../assets/icons/phone.svg";
import phoneWhiteIcon from "../../../../assets/icons/phoneWhite.svg";
import userInputIcon from "../../../../assets/icons/userInput.svg";
import userInputWhiteIcon from "../../../../assets/icons/userInputWhite.svg";

import styles from "./EntriesTable.module.css";
import { formatTime } from "./formatTime";
import type {
	EntriesTableMeta,
	Entry,
	Matter,
	SortDirection,
	SortingMode,
} from "./types";

const APP_ICON_MAP: Record<string, string> = {
	"Microsoft Word": wordIcon,
	"Microsoft Outlook": outlookIcon,
	"Microsoft Outlook Calendar": calendarIcon,
	"Adobe Acrobat": adobeIcon,
	"Microsoft Powerpoint": powerpointIcon,
	"Microsoft Excel": excelIcon,
	Zoom: zoomIcon,
	"Google Chrome": chromeIcon,
	"Microsoft Teams": teamsIcon,
	Kofax: kofaxIcon,
	Foxit: foxitIcon,
	Photos: photosIcon,
};

/**
 * Resolve an application name to an icon URL, preferring exact matches first
 * (so "Microsoft Outlook Calendar" wins over "Microsoft Outlook") and falling
 * back to case-insensitive substring matching for backend process-name variants
 * like "ms-teams".
 */
function renderAppIcon(application: string | undefined, darkMode: boolean) {
	if (!application) return null;
	if (application === "Phone")
		return (
			<img
				src={darkMode ? phoneWhiteIcon : phoneIcon}
				alt="Phone"
				className={styles.appLogo}
			/>
		);
	if (application === "User Input")
		return (
			<img
				src={darkMode ? userInputWhiteIcon : userInputIcon}
				alt="User"
				className={styles.appLogo}
			/>
		);
	if (application === "Combine")
		return (
			<img
				src={darkMode ? combineIconWhite : combineIcon}
				alt="Combine"
				className={styles.appLogo}
			/>
		);
	if (application === "Duplicate")
		return (
			<img
				src={darkMode ? duplicateIconWhite : duplicateIcon}
				alt="Duplicate"
				className={styles.appLogo}
			/>
		);
	let icon: string | undefined = APP_ICON_MAP[application];
	if (!icon) {
		const a = application.toLowerCase();
		if (a.includes("calendar")) icon = calendarIcon;
		else if (a.includes("outlook")) icon = outlookIcon;
		else if (a.includes("word")) icon = wordIcon;
		else if (a.includes("adobe") || a.includes("acrobat")) icon = adobeIcon;
		else if (a.includes("powerpnt") || a.includes("powerpoint"))
			icon = powerpointIcon;
		else if (a.includes("excel")) icon = excelIcon;
		else if (a.includes("zoom")) icon = zoomIcon;
		else if (a.includes("chrome")) icon = chromeIcon;
		else if (a.includes("teams")) icon = teamsIcon;
		else if (a.includes("powerpdf") || a.includes("kofax")) icon = kofaxIcon;
		else if (a.includes("photos")) icon = photosIcon;
		else if (a.includes("foxit")) icon = foxitIcon;
	}
	return icon ? (
		<img src={icon} alt={application} className={styles.appLogo} />
	) : null;
}

/**
 * Format a duration in hours to 1 decimal if it's a multiple of 0.1, else 2.
 *
 * Naive `(d * 100) % 10 !== 0` fails for IEEE 754: 0.1 * 100 = 10.000000000000002,
 * so 0.1, 0.2, 0.3, etc. were incorrectly displayed with 2 decimals. We round to
 * one decimal and compare back with a tolerance.
 */
export function formatDuration(duration: number): string {
	const tenths = duration * 10;
	const isMultipleOf01 = Math.abs(tenths - Math.round(tenths)) < 1e-9;
	return isMultipleOf01 ? duration.toFixed(1) : duration.toFixed(2);
}

/**
 * Returns true if the given entry would render any badge in the status column.
 * Used by EntriesTable to decide whether the status column should be shown at
 * all (it stays hidden when nothing in the visible data needs flagging).
 *
 * Keep this in sync with the status cell's branching logic below.
 */
export function entryHasStatusBadge(
	entry: Entry,
	mode: "entries" | "history" | "deleted",
	matters: Matter[],
	exportingEntryIds: Set<number>,
	failedExportEntries: Map<number, string>,
): boolean {
	const numId = Number(entry.id);
	if (exportingEntryIds.has(numId) || exportingEntryIds.has(entry.id))
		return true;
	if (entry.status) return true; // explicit status from server always rendered
	if (mode === "history" || mode === "deleted") return true;
	if (failedExportEntries.has(numId) || failedExportEntries.has(entry.id))
		return true;
	if (isEntryIncomplete(entry, matters)) return true;
	return false;
}

export function isEntryIncomplete(entry: Entry, matters: Matter[]): boolean {
	const taskRequired = isTaskRequired(entry.matter_id, matters);
	const activityRequired = isActivityRequired(entry.matter_id, matters);
	return (
		!entry.matter ||
		!entry.description ||
		(taskRequired && !entry.task) ||
		(activityRequired && !entry.activity) ||
		entry.description === "Please fill in the narrative."
	);
}

function sortIcon(
	column: SortingMode,
	mode: SortingMode,
	direction: SortDirection,
) {
	if (mode !== column) return <ArrowDownUp className="h-3 w-3" />;
	return direction === "desc" ? (
		<ArrowDown className="h-3 w-3" />
	) : (
		<ArrowUp className="h-3 w-3" />
	);
}

function sortTooltipText(
	column: SortingMode,
	mode: SortingMode,
	direction: SortDirection,
) {
	if (mode !== column)
		return column === "time" ? "Sort by time" : "Sort by matter";
	if (column === "time")
		return direction === "desc" ? "Newest first" : "Oldest first";
	return direction === "asc" ? "A to Z" : "Z to A";
}

/**
 * Internal narrative cell — written as a component so React hooks (useMemo) are legal.
 * Resolves restricted words scoped by the entry's client/matter.
 */
function NarrativeCell({
	entry,
	meta,
}: {
	entry: Entry;
	meta: EntriesTableMeta;
}) {
	const restrictedWords = useMemo(() => {
		if (!meta.getRestrictedWords) return undefined;
		if (!entry.matter_id) return meta.getRestrictedWords();
		const [client, ...rest] = entry.matter_id.split(".");
		const matter = rest.join(".");
		return meta.getRestrictedWords(client, matter);
	}, [meta.getRestrictedWords, entry.matter_id]);

	return (
		<>
			{entry.attorney_feedback && (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className={styles.feedbackWarning}>
								<AlertTriangle className={styles.warningIcon} />
							</span>
						</TooltipTrigger>
						<TooltipContent side="top">
							<p>This narrative has unresolved feedback</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)}
			<HighlightedNarrative
				text={entry.description}
				restrictedWords={restrictedWords}
			/>
		</>
	);
}

export const entriesColumns: ColumnDef<Entry>[] = [
	{
		id: "select",
		enableResizing: false,
		enableHiding: false,
		// Just wide enough for the checkbox itself. Selection count moved to app
		// column header (which is locked visible) so it can't be hidden.
		size: 28,
		minSize: 28,
		maxSize: 28,
		meta: { label: "Selection" },
		header: ({ table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			if (!meta.showCheckboxes) return null;
			return (
				<label className={styles.checkboxHitArea}>
					<input
						type="checkbox"
						checked={meta.selectAllChecked}
						ref={(el) => {
							if (el) el.indeterminate = meta.selectAllIndeterminate;
						}}
						onChange={meta.onToggleSelectAll}
					/>
				</label>
			);
		},
		// Body cell is rendered directly by Row using isChecked prop (see EntriesTable.tsx).
		// We keep this column def for layout (allocates the grid slot) and for the
		// select-all header above; the body cell function is a noop.
		cell: () => null,
	},
	{
		id: "app",
		enableResizing: false,
		// Locked visible so the selection count (rendered in this column's header)
		// stays accessible regardless of user toggling.
		enableHiding: false,
		size: 36,
		minSize: 36,
		maxSize: 36,
		meta: { label: "App" },
		header: ({ table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			const count = meta.checkedRows.size;
			if (count === 0) return null;
			return <span className={styles.selectionCount}>{count}</span>;
		},
		cell: ({ row, table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			return renderAppIcon(row.original.application, meta.darkMode);
		},
	},
	{
		id: "date",
		enableResizing: true,
		enableHiding: true,
		size: 100,
		minSize: 80,
		maxSize: 200,
		meta: { label: "Date" },
		header: ({ table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			return (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="inline-flex items-center gap-1">
								Date {sortIcon("time", meta.sortingMode, meta.sortDirection)}
							</span>
						</TooltipTrigger>
						<TooltipContent>
							<p>
								{sortTooltipText("time", meta.sortingMode, meta.sortDirection)}
							</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			);
		},
		cell: ({ row }) => {
			const entry = row.original;
			return (
				<div className={styles.dateTimeWrapper}>
					<span>{entry.date}</span>
					{entry.end_time && (
						<span className={styles.timeRange}>
							{formatTime(entry.end_time)}
						</span>
					)}
				</div>
			);
		},
	},
	{
		id: "matter",
		enableResizing: true,
		enableHiding: false,
		size: 280,
		minSize: 140,
		maxSize: 500,
		meta: { label: "Matter" },
		header: ({ table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			return (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="inline-flex items-center gap-1">
								Matter{" "}
								{sortIcon("matter", meta.sortingMode, meta.sortDirection)}
							</span>
						</TooltipTrigger>
						<TooltipContent>
							<p>
								{sortTooltipText(
									"matter",
									meta.sortingMode,
									meta.sortDirection,
								)}
							</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			);
		},
		cell: ({ row }) => {
			const entry = row.original;
			return (
				<div className="flex flex-col gap-0.5">
					<div>
						{entry.matter_name}
						{entry.matter ? ` - ${entry.matter}` : ""}
					</div>
					{entry.client && (
						<div style={{ opacity: 0.7, fontSize: "0.9em" }}>
							{entry.client}
						</div>
					)}
				</div>
			);
		},
	},
	{
		id: "codes",
		enableResizing: true,
		enableHiding: true,
		size: 90,
		minSize: 70,
		maxSize: 200,
		meta: { label: "Codes" },
		header: () => (
			<div className="flex w-full items-center justify-center">Codes</div>
		),
		cell: ({ row }) => {
			const entry = row.original;
			return (
				<div className="flex flex-col gap-0.5">
					<div>{entry.task || ""}</div>
					{entry.activity && <div>{entry.activity}</div>}
				</div>
			);
		},
	},
	{
		id: "narrative",
		enableResizing: false,
		enableHiding: false,
		meta: { stretch: true, label: "Narrative" },
		// size is ignored visually (stretch column = 1fr in the grid), but TanStack still needs it.
		size: 600,
		header: () => "Narrative",
		cell: ({ row, table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			return <NarrativeCell entry={row.original} meta={meta} />;
		},
	},
	{
		// Status badge column — its visibility is data-driven (toggled on by
		// EntriesTable when at least one row in the view has a badge to display).
		// `enableHiding: false` keeps it out of the user-facing column picker; the
		// visibility override happens through `effectiveVisibility`.
		id: "status",
		enableResizing: false,
		enableHiding: false,
		size: 100,
		minSize: 100,
		maxSize: 100,
		meta: { label: "Status" },
		header: () => null,
		cell: ({ row, table }) => {
			const meta = table.options.meta as EntriesTableMeta;
			const entry = row.original;
			const numId = Number(entry.id);
			const isExporting =
				meta.exportingEntryIds.has(numId) ||
				meta.exportingEntryIds.has(entry.id);
			const failed =
				meta.failedExportEntries.has(numId) ||
				meta.failedExportEntries.has(entry.id);
			const incomplete = isEntryIncomplete(entry, meta.matters);

			const badge = isExporting ? (
				<div className={styles.exportingBadge}>Exporting</div>
			) : entry.status === "exported" ? (
				<div className={styles.exportedBadge}>Exported</div>
			) : entry.status === "deleted" ? (
				<div className={styles.incompleteBadge}>Deleted</div>
			) : entry.status === "tracked" ? (
				<div className={styles.trackedBadge}>Tracked</div>
			) : meta.mode === "history" ? (
				<div className={styles.exportedBadge}>Exported</div>
			) : meta.mode === "deleted" ? (
				<div className={styles.incompleteBadge}>Deleted</div>
			) : failed ? (
				<div className={styles.failedBadge}>Failed</div>
			) : incomplete ? (
				<div className={styles.incompleteBadge}>Incomplete</div>
			) : null;

			return (
				<div className="flex w-full items-center justify-end">{badge}</div>
			);
		},
	},
	{
		id: "units",
		enableResizing: false,
		enableHiding: false,
		// Just the duration pill now — sized to fit it snugly.
		size: 80,
		minSize: 80,
		maxSize: 80,
		meta: { label: "Units" },
		header: () => (
			<div className="flex w-full items-center justify-end">
				<span>Units</span>
			</div>
		),
		cell: ({ row }) => {
			const entry = row.original;
			return (
				<div className="flex w-full items-center justify-end">
					<div className={styles.unitsBadge}>
						{formatDuration(entry.duration)}
					</div>
				</div>
			);
		},
	},
];
